import type { JSX } from 'react';
import { useT } from '../i18n/index.js';
import { Icon } from './Icon.js';
import './ChipCount.css';

/**
 * The wallet / pot number. Chips are integers with no currency symbol — this
 * component is the only formatter the UI uses, so the rule is enforced in one
 * place (design spec §2, PRD §7.3).
 */

const GROUPED = new Intl.NumberFormat('en-US', { useGrouping: true, maximumFractionDigits: 0 });

/**
 * Full precision, grouped. Magnitude only — the sign belongs to ChipCount,
 * which draws a typographic minus (U+2212), not the hyphen Intl would emit.
 * Its sibling below has the same contract; two formatters that disagreed about
 * the sign would be a trap for whoever reached for the wrong one.
 */
export function formatChips(chips: number): string {
  return GROUPED.format(Math.trunc(Math.abs(chips)));
}

/**
 * Compact form for counts that would otherwise blow the HUD apart. Truncated,
 * never rounded up: a wallet must never appear to hold chips it does not hold.
 */
export function compactChips(chips: number): string {
  const n = Math.trunc(Math.abs(chips));
  if (n < 10_000) return GROUPED.format(n);
  const [unit, step] = n >= 1_000_000 ? (['M', 1_000_000] as const) : (['k', 1_000] as const);
  const tenths = Math.floor((n / step) * 10) / 10;
  return `${Number.isInteger(tenths) ? tenths.toFixed(0) : tenths.toFixed(1)}${unit}`;
}

export interface ChipCountProps {
  /** Chips. Always an integer, never a currency amount. */
  value: number;
  size?: 'sm' | 'md' | 'lg';
  /** Settlement deltas carry an explicit sign so a win reads as a win. */
  signed?: boolean;
  tone?: 'default' | 'win' | 'loss' | 'muted' | 'paper';
  /** Off inside a row that already names the unit. */
  glyph?: boolean;
  /** Force the long form (receipts, wallet history) or the short one (HUD). */
  compact?: boolean;
  className?: string;
}

export function ChipCount({
  value,
  size = 'md',
  signed = false,
  tone = 'default',
  glyph = true,
  compact,
  className,
}: ChipCountProps): JSX.Element {
  const t = useT();
  const magnitude = Math.abs(Math.trunc(value));
  const short = compact ?? magnitude >= 10_000;
  const shown = short ? compactChips(magnitude) : formatChips(magnitude);
  const sign = signed ? (value < 0 ? '−' : '+') : value < 0 ? '−' : '';
  // The compact form is lossy; the exact count stays one hover away.
  const exact = t('ui.chips.exact', { sign, n: formatChips(magnitude) });
  // Screen readers pass over a bare U+2212 — a loss would be announced as a
  // win. The sign gets a word of its own on the accessible string.
  const spoken = t(
    sign === '−' ? 'ui.chips.spoken.minus' : sign === '+' ? 'ui.chips.spoken.plus' : 'ui.chips.spoken',
    { n: formatChips(magnitude) },
  );

  const classes = ['chip-count', `chip-count--${size}`];
  if (tone !== 'default') classes.push(`chip-count--${tone}`);
  if (className !== undefined) classes.push(className);

  return (
    <span className={classes.join(' ')} title={exact}>
      {glyph ? <Icon name="chip" size={size === 'lg' ? 20 : size === 'md' ? 15 : 12} className="chip-count__glyph" /> : null}
      {/* The compact form is lossy, and `title` is not reliably announced — so
          the glyphs on screen are decorative and the exact count is what a
          screen reader reads. A wallet must never be read out truncated. */}
      <span aria-hidden="true">
        {sign === '' ? null : <span className="chip-count__sign">{sign}</span>}
        {shown}
      </span>
      <span className="visually-hidden">{spoken}</span>
    </span>
  );
}
